import { useMemo, useState } from 'react';
import 'react-day-picker/dist/style.css';

import { TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { toast } from 'material-react-toastify';

import Button from '@/components/shared/Button/Button';
import { Text } from '@/components/shared/Text/Text';
import { daysOfWeek } from '@/lib/constants';
import WithLoading from '@/lib/hoc/WithLoading/WithLoading';
import { useCoachCabinetStore } from '@/lib/store/coachCabinetStore';
import { isEqual } from '@/lib/utils';
import { CoachSchedule as ICoachSchedule } from '@addmile/backend-client';

import { useCoachSchedule, useUpdateCoachSchedule } from '../hooks/useCoachSchedule';
import './CoachSchedule.style.scss';
import BufferTime from './components/BufferTime/BufferTime';
import CustomHours from './components/CustomHours/CustomHours';
import CustomTimeslotsModal from './components/CustomTimeslotsModal/CustomTimeslotsModal';
import UnavailableHours from './components/UnavailableHours/UnavailableHours';

const CoachSchedule = () => {
  const coach = useCoachCabinetStore((state) => state.coach);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [workingSchedule, setWorkingSchedule] = useState<ICoachSchedule | null>(null);

  const { data: initialSchedule, isLoading } = useCoachSchedule(coach?.id);

  const updateScheduleMutation = useUpdateCoachSchedule();
  const schedule = workingSchedule || initialSchedule;

  if (initialSchedule && !workingSchedule) {
    setWorkingSchedule(initialSchedule);
  }

  const handleUpdateSchedule = async (updatedSchedule: ICoachSchedule) => {
    if (!coach?.id) return;

    updateScheduleMutation.mutate(
      { coachId: coach.id, schedule: updatedSchedule },
      {
        onSuccess: () => {
          toast.success('Schedule saved successfully');
          setWorkingSchedule(updatedSchedule);
        },
        onError: (error) => {
          toast.error(error.message);
        },
      },
    );
  };

  const handleAddCustomHours = (customHours: ICoachSchedule['customHours']) => {
    if (!schedule) return;

    const updatedSchedule = {
      ...schedule,
      customHours: [...(customHours || [])],
    };

    setWorkingSchedule(updatedSchedule);
    handleUpdateSchedule(updatedSchedule);
  };

  const handleTimeChange = (
    day: string,
    index: number,
    field: 'fromTime' | 'toTime',
    value: Dayjs,
  ) => {
    if (!schedule) return;

    const formattedTime = value ? dayjs(value).format('HH:mm') : '';
    const updatedWorkingHours = JSON.parse(JSON.stringify(schedule.workingHours));

    if (!updatedWorkingHours[day][index]) {
      updatedWorkingHours[day][index] = { fromTime: '', toTime: '' };
    }

    updatedWorkingHours[day][index][field] = formattedTime;
    setWorkingSchedule({ ...schedule, workingHours: updatedWorkingHours });
  };

  const addTimeslot = (day: string) => {
    if (!schedule) return;

    const updatedWorkingHours = { ...schedule.workingHours };

    if (!updatedWorkingHours[day]) {
      updatedWorkingHours[day] = [];
    }

    updatedWorkingHours[day].push({ fromTime: '', toTime: '' });
    setWorkingSchedule({ ...schedule, workingHours: updatedWorkingHours });
  };

  const removeTimeslot = (day: string, index: number) => {
    if (!schedule) return;

    const updatedWorkingHours = { ...schedule.workingHours };
    updatedWorkingHours[day].splice(index, 1);

    if (!updatedWorkingHours[day].length) {
      delete updatedWorkingHours[day];
    }

    setWorkingSchedule({ ...schedule, workingHours: updatedWorkingHours });
  };

  const isButtonDisabled = useMemo(() => {
    if (!schedule || !initialSchedule) return true;

    if (isEqual(schedule, initialSchedule)) {
      return true;
    }

    if (!schedule.workingHours) {
      return true;
    }

    return daysOfWeek.some((day) => {
      if (!schedule.workingHours[day]) {
        return false;
      }

      return schedule.workingHours[day].some(
        (timeslots) =>
          !timeslots.fromTime || !timeslots.toTime || timeslots.fromTime >= timeslots.toTime,
      );
    });
  }, [initialSchedule, schedule]);

  if (!coach?.id || !schedule) {
    return null;
  }

  return (
    <WithLoading isLoading={isLoading} className="p-4">
      <div className="flex justify-start gap-24">
        <div className="w-[40rem]">
          <Text.H2 className="font-bold mb-8">Weekly Hours</Text.H2>
          {daysOfWeek.map((day) => (
            <div key={day} className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-semibold">{day}</h3>
              </div>
              {!schedule.workingHours[day] || schedule.workingHours[day]?.length === 0 ? (
                <UnavailableHours addTimeslot={() => addTimeslot(day)} />
              ) : (
                schedule.workingHours[day].map((timeslots, index) => (
                  <div key={`${day}-${index}`} className="flex gap-2 mb-2 items-center">
                    <TimePicker
                      format="HH:mm"
                      value={timeslots.fromTime ? dayjs(timeslots.fromTime, 'HH:mm') : null}
                      onChange={(value) => handleTimeChange(day, index, 'fromTime', value)}
                      maxDate={dayjs(timeslots.toTime, 'HH:mm')}
                    />
                    &#x2015;
                    <TimePicker
                      format="HH:mm"
                      value={timeslots.toTime ? dayjs(timeslots.toTime, 'HH:mm') : null}
                      onChange={(value) => handleTimeChange(day, index, 'toTime', value)}
                      minDate={dayjs(timeslots.fromTime, 'HH:mm')}
                    />
                    <Button
                      isIcon
                      variant="transparent"
                      className="w-8 h-8 border-none"
                      onClick={() => removeTimeslot(day, index)}
                      prefixIcon={<img src={'/icons/close.svg'} alt="Remove" />}
                    />
                    <Button
                      isIcon
                      variant="transparent"
                      className="w-8 h-8 border-none"
                      onClick={() => addTimeslot(day)}
                      prefixIcon={<img src={'/icons/plus.svg'} alt="Add" />}
                    />
                  </div>
                ))
              )}
            </div>
          ))}

          <BufferTime
            bufferMin={schedule.bufferMin}
            timeslotDays={schedule.timeslotDays}
            handleBufferMinChange={(value) =>
              setWorkingSchedule({ ...schedule, bufferMin: value ?? 0 })
            }
            handleTimeslotDaysChange={(value) =>
              setWorkingSchedule({ ...schedule, timeslotDays: value ?? 0 })
            }
          />

          <div className="flex gap-4 mt-8">
            <Button
              variant="primary"
              size="s"
              onClick={() => handleUpdateSchedule(schedule)}
              label={updateScheduleMutation.isPending ? 'Saving...' : 'Save Schedule'}
              disabled={isButtonDisabled || updateScheduleMutation.isPending}
            />
            <Button
              variant="secondary"
              onClick={() => setIsOpenModal(true)}
              className="ml-4"
              size="s"
              label="Add Custom Hours"
              disabled={updateScheduleMutation.isPending}
            />
          </div>
        </div>

        {schedule.customHours && (
          <CustomHours
            customHours={schedule.customHours}
            timezone={coach.timezone}
            onAddCustomHours={handleAddCustomHours}
          />
        )}
      </div>

      <CustomTimeslotsModal
        isPopupOpen={isOpenModal}
        closePopup={() => setIsOpenModal(false)}
        onAddCustomHours={handleAddCustomHours}
        schedule={schedule}
      />
    </WithLoading>
  );
};

export default CoachSchedule;
